import React, { useState, useEffect } from 'react';
import { Bot, Users, Activity, CheckCircle, XCircle, Link as LinkIcon, Copy, Trash2, RefreshCw, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import UnitedBlacklist from './UnitedBlacklist';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TelegramBot = () => {
  const [botInfo, setBotInfo] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupData, setGroupData] = useState({
    grupo_telegram_id: '',
    grupo_telegram_name: ''
  });
  const [newUserData, setNewUserData] = useState({
    telegram_user_id: '',
    telegram_username: '',
    telegram_first_name: '',
    role: 'cliente'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Cargar información del bot
      const botInfoRes = await fetch(`${API}/telegram/bot-info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const botInfoData = await botInfoRes.json();
      setBotInfo(botInfoData);

      // Cargar clientes con sus grupos de Telegram
      const clientesRes = await fetch(`${API}/clientes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const clientesData = await clientesRes.json();
      
      // Para cada cliente, obtener su grupo de Telegram
      const clientesConGrupos = await Promise.all(
        clientesData.map(async (cliente) => {
          try {
            const groupRes = await fetch(`${API}/clientes/${cliente.id}/telegram-group`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const groupData = await groupRes.json();
            return { ...cliente, telegramGroup: groupData };
          } catch (error) {
            return { ...cliente, telegramGroup: null };
          }
        })
      );
      
      setClientes(clientesConGrupos);

      // Cargar actividad reciente
      const activityRes = await fetch(`${API}/telegram/activity?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const activityData = await activityRes.json();
      setActivity(activityData);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGroup = async () => {
    if (!groupData.grupo_telegram_id || !selectedCliente) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/clientes/${selectedCliente.id}/telegram-group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(groupData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al conectar grupo');
      }

      toast.success('Grupo conectado exitosamente');
      setShowConnectDialog(false);
      setGroupData({ grupo_telegram_id: '', grupo_telegram_name: '' });
      setSelectedCliente(null);
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDisconnectGroup = async (clienteId) => {
    if (!window.confirm('¿Estás seguro de desconectar este grupo?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/clientes/${clienteId}/telegram-group`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al desconectar');

      toast.success('Grupo desconectado');
      loadData();
    } catch (error) {
      toast.error('Error al desconectar grupo');
    }
  };

  const handleAssignRole = async (telegramUserId, role) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/clientes/${selectedCliente.id}/telegram-group/assign-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          telegram_user_id: telegramUserId,
          role: role
        })
      });

      if (!response.ok) throw new Error('Error al asignar rol');

      toast.success('Rol asignado exitosamente');
      loadData();
      
      // Actualizar el grupo seleccionado
      const updatedCliente = clientes.find(c => c.id === selectedCliente.id);
      if (updatedCliente) {
        setSelectedGroup(updatedCliente.telegramGroup);
      }
    } catch (error) {
      toast.error('Error al asignar rol');
    }
  };

  const handleAddUserManual = async () => {
    if (!newUserData.telegram_user_id || !newUserData.telegram_first_name) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/clientes/${selectedCliente.id}/telegram-group/add-user-manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          telegram_user_id: parseInt(newUserData.telegram_user_id),
          telegram_username: newUserData.telegram_username || null,
          telegram_first_name: newUserData.telegram_first_name,
          role: newUserData.role
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al agregar usuario');
      }

      toast.success('Usuario agregado exitosamente');
      setShowAddUserDialog(false);
      setNewUserData({
        telegram_user_id: '',
        telegram_username: '',
        telegram_first_name: '',
        role: 'cliente'
      });
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyBotUsername = () => {
    if (botInfo?.bot_username) {
      navigator.clipboard.writeText(`@${botInfo.bot_username}`);
      toast.success('Username copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Bot className="w-8 h-8 text-blue-600" />
            Bot de Telegram
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestiona la integración del bot con tus clientes
          </p>
        </div>
        <Button onClick={loadData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Estado del Bot */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {botInfo?.bot_configured ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
            Estado del Bot
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bot Configurado</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-semibold text-lg">@{botInfo?.bot_username || 'Evogametech_bot'}</p>
                {botInfo?.bot_username && (
                  <Button variant="ghost" size="sm" onClick={copyBotUsername}>
                    <Copy className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Agrega este bot a tus grupos de Telegram
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Estado</p>
              <p className="font-semibold mt-1">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                  botInfo?.status === 'running' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {botInfo?.status === 'running' ? 'Activo' : 'Inactivo'}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Grupos Conectados</p>
              <p className="font-semibold text-2xl mt-1">{botInfo?.active_groups || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cartera United · Blacklist de @usernames */}
      <UnitedBlacklist />

      {/* Clientes y Grupos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Clientes con Bot de Telegram</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Cartera</TableHead>
                <TableHead>Grupo Telegram</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientes.map((cliente) => (
                <TableRow key={cliente.id}>
                  <TableCell className="font-medium">{cliente.name}</TableCell>
                  <TableCell>{cliente.cartera_name || 'N/A'}</TableCell>
                  <TableCell>
                    {cliente.telegramGroup ? (
                      <div>
                        <p className="font-medium">{cliente.telegramGroup.grupo_telegram_name || 'Sin nombre'}</p>
                        <p className="text-xs text-gray-500">ID: {cliente.telegramGroup.grupo_telegram_id}</p>
                      </div>
                    ) : (
                      <span className="text-gray-400">No conectado</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {cliente.telegramGroup ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3" />
                        Conectado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                        <XCircle className="w-3 h-3" />
                        Sin conectar
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {cliente.telegramGroup ? (
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedCliente(cliente);
                            setSelectedGroup(cliente.telegramGroup);
                            setShowUsersDialog(true);
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Usuarios
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDisconnectGroup(cliente.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedCliente(cliente);
                          setShowConnectDialog(true);
                        }}
                      >
                        <LinkIcon className="w-4 h-4 mr-1" />
                        Conectar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Actividad Reciente */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Actividad Reciente
          </CardTitle>
          <CardDescription>Últimos comprobantes procesados por el bot</CardDescription>
        </CardHeader>
        <CardContent>
          {activity.length > 0 ? (
            <div className="space-y-2">
              {activity.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{item.cliente_name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {item.grupo_telegram_name} • {item.action}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">${item.amount?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(item.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No hay actividad reciente</p>
          )}
        </CardContent>
      </Card>

      {/* Dialog para gestionar usuarios del grupo */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gestionar Usuarios del Grupo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Cliente:</strong> {selectedCliente?.name}
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Grupo:</strong> {selectedGroup?.grupo_telegram_name || 'Sin nombre'}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Asignar Roles a Usuarios</h3>
                <Button
                  size="sm"
                  onClick={() => setShowAddUserDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Usuario
                </Button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                👤 <strong>Cliente:</strong> Sus comprobantes SUMAN al balance<br/>
                💼 <strong>Admin/Financiera:</strong> Sus comprobantes RESTAN (son pagos)<br/>
                ❓ <strong>Sin asignar:</strong> El bot no procesa sus fotos
              </p>

              {selectedGroup?.users_telegram && selectedGroup.users_telegram.length > 0 ? (
                <div className="space-y-2">
                  {selectedGroup.users_telegram.map((user, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium">
                          {user.telegram_first_name || 'Usuario'} 
                          {user.telegram_username && ` (@${user.telegram_username})`}
                        </p>
                        <p className="text-xs text-gray-500">ID: {user.telegram_user_id}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={user.role === 'cliente' ? 'default' : 'outline'}
                          onClick={() => handleAssignRole(user.telegram_user_id, 'cliente')}
                          title="Marcar como Cliente"
                        >
                          👤 Cliente
                        </Button>
                        <Button
                          size="sm"
                          variant={user.role === 'admin' ? 'default' : 'outline'}
                          onClick={() => handleAssignRole(user.telegram_user_id, 'admin')}
                          title="Marcar como Admin/Financiera"
                        >
                          💼 Admin
                        </Button>
                        <Button
                          size="sm"
                          variant={!user.role ? 'default' : 'outline'}
                          onClick={() => handleAssignRole(user.telegram_user_id, null)}
                          title="Quitar rol"
                        >
                          ❌
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No hay usuarios registrados en este grupo</p>
                  <p className="text-sm mt-2">Los usuarios aparecerán aquí cuando envíen mensajes en el grupo</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowUsersDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para agregar usuario manualmente */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Usuario Manualmente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                💡 <strong>Cómo obtener el Telegram User ID:</strong><br/>
                1. Agrega @userinfobot a tu contactos<br/>
                2. Envía cualquier mensaje<br/>
                3. El bot responderá con: "Id: 123456789"
              </p>
            </div>
            
            <div>
              <Label>Telegram User ID *</Label>
              <Input
                type="number"
                placeholder="123456789"
                value={newUserData.telegram_user_id}
                onChange={(e) => setNewUserData({...newUserData, telegram_user_id: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Nombre *</Label>
              <Input
                placeholder="Juan Pérez"
                value={newUserData.telegram_first_name}
                onChange={(e) => setNewUserData({...newUserData, telegram_first_name: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Username de Telegram (opcional)</Label>
              <Input
                placeholder="@juanperez (sin @)"
                value={newUserData.telegram_username}
                onChange={(e) => setNewUserData({...newUserData, telegram_username: e.target.value.replace('@', '')})}
              />
            </div>
            
            <div>
              <Label>Rol</Label>
              <select
                className="w-full p-2 border rounded-md"
                value={newUserData.role}
                onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
              >
                <option value="cliente">👤 Cliente (comprobantes suman)</option>
                <option value="admin">💼 Admin/Financiera (comprobantes restan)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddUserManual}>
              Agregar Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para conectar grupo */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conectar Grupo de Telegram</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Cliente</Label>
              <Input value={selectedCliente?.name || ''} disabled />
            </div>
            <div>
              <Label>ID del Grupo de Telegram</Label>
              <Input
                placeholder="-1001234567890"
                value={groupData.grupo_telegram_id}
                onChange={(e) => setGroupData({...groupData, grupo_telegram_id: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">
                Agrega el bot al grupo y usa el comando /getgroupid para obtener el ID
              </p>
            </div>
            <div>
              <Label>Nombre del Grupo (opcional)</Label>
              <Input
                placeholder="Grupo Cliente Juan"
                value={groupData.grupo_telegram_name}
                onChange={(e) => setGroupData({...groupData, grupo_telegram_name: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConnectGroup}>
              Conectar Grupo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TelegramBot;
